import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { get, map, sumBy, flatten } from 'lodash-es';
import ReactLoading from 'react-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import {
  faPlus,
  faEnvelope,
  faStickyNote,
  faEllipsisV,
  faQuestionCircle,
  faCreditCard,
  faTruck,
} from '@fortawesome/free-solid-svg-icons';
import { Button,
  Col,
  Container,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Input,
  Row,
  Table,
  UncontrolledTooltip,
} from 'reactstrap';
import classNames from 'classnames';
import { CollectionsBookmarkOutlined } from '@mui/icons-material';
import { preformatFloat, formatCurrencyForLocale, getInvoiceDetailStatus, formatDateString, allVisibleArePaid, formatDate } from '../../../utilities/invoiceUtils';
import { useAxios, useIsComponentMounted } from '../../../hooks';
import { formatChannelText, titleCase } from '../../../utilities/textsFormatters';
import Page from '../../Page';
import AddChargesModal from '../AddChargesModal';
import AddNoteModal from '../AddNoteModal';
import ContactModal from '../ContactModal';
import CalendarModal from '../CalendarModal';
import { cfPath, awsServer } from '../../../utilities/UrlPaths';
import AdditionalChargesTable from './AdditionalChargesTable';
import InvaluableShippingBanner from '../InvaluableShippingBanner/InvaluableShippingBanner';
import { selectPolicyID, selectPolicyTerms, selectHouseID } from '../../../selectors';
import { SHIPPING_METHODS_MAP } from '../constants';

const blockEl = 'invoices__edit-invoices';

const EditByLot = () => {
  const [invoices, setInvoices] = useState([]);
  const [isOpenInvoiceItemStatusMenu, setIsOpenInvoiceItemStatusMenu] = useState({});
  const [currentInvoiceIndex, setCurrentInvoiceIndex] = useState(null);
  const [isAddChargesModalOpen, setIsAddChargesModalOpen] = useState(false);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [invoicesNotes, setInvoicesNotes] = useState({});
  const [calendarID, setCalendarID] = useState(0);
  const [calendarDetailID, setCalendarDetailID] = useState(0);
  const [calendarVariant, setCalendarVariant] = useState('');
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [calendarPaymentMethod, setCalendarPaymentMethod] = useState(0);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarCurrentStatus, setCalendarCurrentStatus] = useState(false);
  const [checkedInvoices, setCheckedInvoices] = useState({});
  const [checkedAllInvoicesIndicator, setCheckAllInvoicesIndicator] = useState(true);
  const [noneSelected, setNoneSelected] = useState(true);
  const [disabledCursorClass, setDisabledCursorClass] = useState('disabled-cursor');
  const [allOrSelected, setAllOrSelected] = useState('All');
  const [topTotals, setTopTotals] = useState({});
  const [lotVariant, setLotVariant] = useState('lot');
  const [topTotalsData, setTopTotalsData] = useState([]);
  const [invoicesCharges, setInvoicesCharges] = useState([]);
  const [randomNumber, setRandomNumber] = useState(0);
  const [pickupShipUpdateObject, setPickupShipUpdateObject] = useState({});
  const [defaultShippingMethods, setDefaultShippingMethods] = useState('');
  const [auctions, setAuctions] = useState([]);
  const [shipmentsMap, setShipmentsMap] = useState({});

  const { invoiceGroupID } = useParams();
  const history = useHistory();
  const { houseRef, paymentProcessingEnabled, autoChargeEnabled, houseID, houseUserName } = useSelector(state => state.house);
  const { processorType } = useSelector(state => state.invoice);
  const paymentProcessorType = processorType;
  const user = useSelector(state => state.user);
  const isInitialInvoicesDataLoaded = useRef();
  const isComponentMounted = useIsComponentMounted();
  const queryParams = new URLSearchParams(window.location.search);
  const queryID = queryParams.get('id') || '';
  const notSetText = 'not set';
  const isInvShippingAvailable = useSelector(selectPolicyID);
  const policyTerms = useSelector(selectPolicyTerms);

  const {
    isIdle,
    isLoading,
    data: groupDetailInfoData,
    exec: execGroupDetailInfo
  } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfo`,
    [houseRef],
    { method: 'get' },
    { manual: true }
  );
  const {
    data: editPageData,
    exec: execEditPageData
  } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfoByInvoice`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { exec: execNotes, isError: isNotesError } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfo`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { exec: execMessage } = useAxios(
    () => `/app/house/${houseRef}/invoiceMessage`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );

  const { data: paymentMethodsData } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/payment-methods`,
    [houseRef]
  );
  const { exec: execPaidDate } = useAxios(
    () => `/app/house/${houseRef}/invoicePaidOnlyByDetail`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { exec: execShippedDate } = useAxios(
    () => `/app/house/${houseRef}/invoiceShippedOnlyByDetail`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { execAsync: execAsyncAdditionalCharges } = useAxios(
    () => `/app/house/${houseRef}/additionalCharges`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { exec: execPickupShip, data: pickupShipData } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfo`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );

  const showLoader = !isInitialInvoicesDataLoaded.current && (isIdle || isLoading);

  useEffect(() => {
    if (queryID.length) {
      execEditPageData({
        data: queryID.split(','),
      });
    } else {
      execGroupDetailInfo();
    }
  }, []);

  useEffect(() => {
    if (isComponentMounted.current) {
      if (typeof groupDetailInfoData === 'undefined') {
        return;
      }
      isInitialInvoicesDataLoaded.current = true;
      setInvoices(get(groupDetailInfoData, 'data.data.invoiceBidders', []));
      setAuctions(get(groupDetailInfoData, 'data.data.catalogs', []));
      const addlObject = get(groupDetailInfoData, 'data.data.totalPriceWithAdditionalCharges', {});
      const totalObject = get(groupDetailInfoData, 'data.data.totalPrice', {});
      setTopTotalsData(addlObject === null ? totalObject : addlObject);
    }
  }, [groupDetailInfoData]);

  useEffect(() => {
    if (isComponentMounted.current) {
      if (typeof editPageData === 'undefined') {
        return;
      }

      isInitialInvoicesDataLoaded.current = true;
      setInvoices(get(editPageData, 'data.data.invoiceBidders', []));
      setAuctions(get(editPageData, 'data.data.catalogs', []));
      const addlObject = get(editPageData, 'data.data.totalPriceWithAdditionalCharges', {});
      const totalObject = get(editPageData, 'data.data.totalPrice', {});
      setTopTotalsData(addlObject === null ? totalObject : addlObject);
    }
  }, [editPageData]);

  useEffect(() => {
    if (typeof paymentMethodsData === 'undefined') {
      return;
    }

    const methods = get(paymentMethodsData, 'data.data.paymentMethods', []);

    setPaymentMethods(methods);
  }, [paymentMethodsData]);

  useEffect(() => {
    if (isNotesError) {
      setInvoicesNotes((prev) => {
        const { [`_${currentInvoiceIndex}`]: _, ...restInvoicesNotes } = prev;

        return restInvoicesNotes;
      });
    }
  }, [isNotesError]);

  useEffect(() => {
    if (invoices.length > 0) setCurrencySymbol(invoices[0].totalPrice.currencySymbol);
  }, [invoices]);

  const fetchShipment = async (id) => {
    const controller = new AbortController();
    try {
      const response = await axios.get(`/api/shipping/shipment/${id}`, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'x-invaluable-shipping-account-id': `${houseID}`,
          'x-invaluable-shipping-account-domain': 'AUCTIONHOUSE'
        }
      });
      const method = response.data.data.shipment.arguments.option.shippingMethod;

      const shipObject = shipmentsMap;
      shipObject[id] = method;
      setShipmentsMap(shipObject);

      controller.abort();
      return method;
    } catch (error) {
      controller.abort();
      console.error(error);
    }
  };

  const getShippingMethod = (shipID) => {
    let method = shipmentsMap[shipID];
    if (method === '' || method === undefined) {
      method = fetchShipment(shipID);
      return method;
    }
    return method;
  };

  const formatShippingMethod = (shipID) => {
    const method = shipID ? getShippingMethod(shipID) : '';
    if (method) {
      if (method === 'invaluable_shipping') {
        return (
          <>
            <img src={`${awsServer}/static/invShippingIcon.png`} alt="shipping truck" className="shippingTruckIcon height-20" />
            <span className="pl-2">
              Inv Ship
            </span>
          </>
        );
      }
      return SHIPPING_METHODS_MAP[method];
    }
    return (
      <span className="italic-text">Not Selected</span>
    );
  };

  useEffect(() => {
    if (invoices.length) {
      const shipmentObject = {};
      invoices.map(invoice => {
        invoice.invoiceDetails.map((item) => {
          if (item.shippingShipmentID !== '' && !shipmentObject.hasOwnProperty(item.shippingShipmentID)) {
            shipmentObject[item.shippingShipmentID] = '';
          }
        });
      });
      setShipmentsMap(shipmentObject);
    }
  }, [invoices]);

  const getOutstandingBalancePerInvoice = useCallback(
    (invoiceId) => {
      const currentInvoice = invoices.find(
        (invoice) => invoice.invoiceID === invoiceId
      );

      if (!currentInvoice) {
        return '0.00';
      }

      const { invoiceDetails } = currentInvoice;

      if (
        typeof invoiceDetails === 'undefined'
      || (Array.isArray(invoiceDetails) && !invoiceDetails.length)
      ) {
        return '0.00';
      }

      let outstandingBal = '0.00';
      if (!currentInvoice.allPaid) {
        outstandingBal = sumBy(
          invoiceDetails,
          (item) => parseFloat((item.paid || item.suppress) ? 0 : item.totalAmount || 0)
        );
      }

      return formatCurrencyForLocale(outstandingBal);
    },
    [invoices]
  );

  const getInvoiceTotalHeader = useCallback(
    (invoiceId) => {
      const currentInvoice = invoices.find(
        (invoice) => invoice.invoiceID === invoiceId
      );

      if (!currentInvoice) {
        return '0.00';
      }

      const { invoiceDetails } = currentInvoice;

      if (
        typeof invoiceDetails === 'undefined'
      || (Array.isArray(invoiceDetails) && !invoiceDetails.length)
      ) {
        return '0.00';
      }

      let totalAmount = 0;
      invoiceDetails.forEach((invoice) => {
        if (!invoice.suppress) {
          totalAmount += parseFloat(invoice.totalAmount) || 0;
        }
      });

      return formatCurrencyForLocale(totalAmount);
    },
    [invoices]
  );

  const invoiceHasRestrictedItems = (id) => {
    const currentInvoice = invoices.find(
      (invoice) => invoice.invoiceID === id
    );
    const { invoiceDetails } = currentInvoice;

    let returnFlag = false;
    invoiceDetails.forEach((item) => {
      if (get(item, 'restricted', false)) {
        returnFlag = true;
      }
    });

    return returnFlag;
  };

  const canEditInvoiceCharges = ({ allPaid, applyCostPerLot, costsOverride }) => !allPaid && (applyCostPerLot || costsOverride);

  const showEditAddChargesToAllButton = useMemo(() => {
    if (typeof invoices === 'undefined' || (Array.isArray(invoices) && !invoices.length)) {
      return false;
    }

    return invoices.some(canEditInvoiceCharges);
  }, [invoices]);

  const currentInvoiceNotes = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length || typeof invoices[currentInvoiceIndex] === 'undefined') {
      return '';
    }

    return invoicesNotes[`_${currentInvoiceIndex}`] || invoices[currentInvoiceIndex].notes || '';
  }, [invoices, currentInvoiceIndex, invoicesNotes]);

  const allInvoicesIds = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length) {
      return [];
    }

    return map(invoices, 'invoiceID');
  }, [invoices]);

  const modalNameObject = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length || typeof invoices[currentInvoiceIndex] === 'undefined') {
      return '';
    }

    return {
      name: `${invoices[currentInvoiceIndex].lastName}, ${invoices[currentInvoiceIndex].firstName}`,
      paddleNumber: invoices[currentInvoiceIndex].paddleNumber,
      id: invoices[currentInvoiceIndex].invoiceID,
      location: `${invoices[currentInvoiceIndex].city}, ${invoices[currentInvoiceIndex].state}${invoices[currentInvoiceIndex].province} ${invoices[currentInvoiceIndex].country}`,
      channel: invoices[currentInvoiceIndex].channel,
    };
  }, [invoices, currentInvoiceIndex]);

  const isUsPayrixUser = (id) => {
    const currentInvoice = invoices.find(
      (invoice) => invoice.invoiceID === id
    );
    return (paymentProcessorType === 'p' && currentInvoice.country === 'US');
  };

  const handleOnSaveInvoiceCharges = async (invoice, isAddl) => {
    // first check for changes and create updatedCharges array
    const currentInvoice = invoices.find(
      (myInvoice) => myInvoice.invoiceID === invoice.id
    );
    const { invoiceDetails } = currentInvoice;
    const updatedCharges = [];
    const updatedFloorCharges = [];

    invoice.charges.forEach(lot => {
      // get original lot data
      const currentLot = invoiceDetails.find(
        (myDetail) => myDetail.lotID === lot.lotId
      );
      //  account for first addl charges or changes
      if (
        (currentLot.priceInfo.tax !== lot.tax
          || currentLot.priceInfo.shipping !== lot.shipping
          || currentLot.priceInfo.insurance !== lot.insurance
          || currentLot.priceInfo.other !== lot.other)
        || (currentLot.additionalPriceInfo !== null && (currentLot.additionalPriceInfo.tax !== lot.additionalTax
          || currentLot.additionalPriceInfo.shipping !== lot.additionalShipping
          || currentLot.additionalPriceInfo.insurance !== lot.additionalInsurance
          || currentLot.additionalPriceInfo.other !== lot.additionalOther))
        || (currentLot.additionalPriceInfo === null
          && (lot.additionalTax > 0
            || lot.additionalShipping > 0
            || lot.additionalInsurance > 0
            || lot.additionalOther > 0))
      ) {
        updatedCharges.push(lot);
      }
      // floor charges
      if (currentLot.priceInfo.hammer !== lot.hammer
        || currentLot.priceInfo.premium !== lot.premium
        || currentLot.priceInfo.livefee !== lot.livefee) {
        updatedFloorCharges.push(lot);
      }
    });

    try {
      if (updatedCharges.length > 0) {
        const res = await Promise.all(
          updatedCharges.map(
            ({ lotDisplayName, lotId, ...restCharges }) => execAsyncAdditionalCharges({
              data: {
                suppress: restCharges.suppress,
                restricted: restCharges.restricted,
                paid: restCharges.paid,
                ...(!isUsPayrixUser(invoice.id) && { tax: isAddl ? parseFloat(preformatFloat(restCharges.additionalTax)) : parseFloat(preformatFloat(restCharges.tax)) }),
                shipping: isAddl ? parseFloat(preformatFloat(restCharges.additionalShipping)) : parseFloat(preformatFloat(restCharges.shipping)),
                insurance: isAddl ? parseFloat(preformatFloat(restCharges.additionalInsurance)) : parseFloat(preformatFloat(restCharges.insurance)),
                other: isAddl ? parseFloat(preformatFloat(restCharges.additionalOther)) : parseFloat(preformatFloat(restCharges.other)),
                invoiceID: invoice.id,
                lotID: lotId,
                additionalCharge: isAddl
              },
              headers: { Authorization: `${houseRef}*` }
            })
          )
        );
      }

      if (updatedFloorCharges.length > 0) {
        //  build the payload
        const lotObjects = [];
        updatedFloorCharges.map(lot => {
          const thisLot = {
            lotId: lot.lotId,
            hammer: parseFloat(preformatFloat(lot.hammer)),
            buyersPremium: parseFloat(preformatFloat(lot.premium)),
            liveFee: parseFloat(preformatFloat(lot.livefee)),
          };
          lotObjects.push(thisLot);
        });

        const data = {
          type: 'invoice',
          id: invoice.id,
          attributes: {
            invoiceDetails: lotObjects,
          }
        };

        const postBody = { data };
        const postHeaders = {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            channel: 'infinitebidding',
            houseusername: houseUserName,
            Authorization: `${houseRef}*`
          }
        };

        await axios.patch(`/app/house/${houseRef}/invoice/${invoice.id}`, JSON.stringify(postBody), postHeaders);
      }
    } catch (error) {
      console.log('Error', error);
    }
    if (queryID.length) {
      execEditPageData({
        data: queryID.split(','),
      });
    } else {
      execGroupDetailInfo();
    }
  };

  const handleOnSetCurrentInvoiceIndex = (index) => () => {
    if (index === currentInvoiceIndex) {
      return;
    }

    setCurrentInvoiceIndex(index);
  };

  const handleOnEditAddChargesToAll = () => {
    setCurrentInvoiceIndex(null);
    setIsAddChargesModalOpen(true);
  };

  const handleOnAddNote = (notes) => {
    const currentInvoice = invoices[currentInvoiceIndex];

    if (
      !notes
      || typeof currentInvoice === 'undefined'
      || !currentInvoice.invoiceID
    ) {
      return;
    }

    execNotes({
      data: {
        invoiceNotes: [
          {
            notes,
            invoiceID: currentInvoice.invoiceID,
          },
        ],
      },
    });

    setTimeout(() => {
      const icon = document.getElementById(`noteIcon_${currentInvoice.invoiceID}`);
      icon.classList.add('has-note');
    }, 500);

    setInvoicesNotes((prev) => ({
      ...prev,
      [`_${currentInvoiceIndex}`]: notes
    }));
    setIsAddNoteModalOpen(false);
  };

  const handleOnSendMessage = (payload) => {
    const currentInvoice = invoices[currentInvoiceIndex];

    if (
      typeof houseRef === 'undefined'
      || typeof currentInvoice === 'undefined'
      || !currentInvoice.memberID
      || typeof payload !== 'object'
      || !Object.keys(payload).length
    ) {
      return;
    }

    execMessage({
      data: {
        ...payload,
        houseUserRef: user.userRef,
        bidderID: currentInvoice.memberID
      },
    });
    setIsContactModalOpen(false);
  };

  const handleOnOpenInvoiceItemStatusMenu = (index) => () => {
    setIsOpenInvoiceItemStatusMenu((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const showColumn = (columnName, id) => {
    let returnFlag = false;
    let currentValue = 0;
    invoices.forEach((currentInvoice) => {
      if (currentInvoice.invoiceID === id) {
        currentInvoice.invoiceDetails.forEach((item) => {
          currentValue = item.priceInfo[columnName] || '0.00';
          if (currentValue !== '0.00') {
            returnFlag = true;
          }
        });
      }
    });
    return returnFlag;
  };

  const showDisputesColumn = (id) => {
    let returnFlag = false;
    invoices.forEach((currentInvoice) => {
      if (currentInvoice.invoiceID === id) {
        currentInvoice.invoiceDetails.forEach((item) => {
          if (item.disputeRef) {
            returnFlag = true;
          }
        });
      }
    });
    return returnFlag;
  };

  const openCalendar = (e) => {
    setCalendarID(Number(e.currentTarget.attributes.invoiceid.value));
    setCalendarDetailID(Number(e.currentTarget.attributes.detailid.value));
    setCalendarVariant(e.currentTarget.attributes.variant.value);
    setCalendarDate(e.currentTarget.attributes.date.value);
    setCalendarPaymentMethod(e.currentTarget.attributes.paymentmethodid.value || 0);
    // Current Status is true if this invoice is paid or shipped (based on variant)
    setCalendarCurrentStatus(e.currentTarget.attributes.currentstatus.value === 'true');
    setIsCalendarModalOpen(true);
  };

  const handleSetShippedDate = (postData) => {
    execShippedDate({
      data: {
        paidInvoices: [postData],
      }
    });

    const invoicesCopy = invoices;
    invoicesCopy.forEach((currentInvoice) => {
      if (currentInvoice.invoiceID === postData.invoiceID) {
        currentInvoice.invoiceDetails.forEach((item) => {
          if (item.invoiceDetailID === postData.invoiceDetailID) {
            item.shipped = true;
            item.shippedDateString = postData.shippedDate;
          }
        });
      }
    });
    setInvoices(invoicesCopy);

    setIsCalendarModalOpen(false);
  };

  const handleSetPaidDate = (postData) => {
    execPaidDate({
      data: {
        paidInvoices: [postData],
      }
    });

    const invoicesCopy = invoices;
    invoicesCopy.forEach((currentInvoice) => {
      if (currentInvoice.invoiceID === postData.invoiceID) {
        currentInvoice.invoiceDetails.forEach((item) => {
          if (item.invoiceDetailID === postData.invoiceDetailID) {
            item.paid = postData.paid;
            item.paidDateString = postData.paidDate;
          }
        });
      }
    });
    setInvoices(invoicesCopy);

    setIsCalendarModalOpen(false);
  };

  const hideShowItem = async (e) => {
    const type = e.currentTarget.id.substring(0, 4);
    const invoiceDetailID = e.currentTarget.id.substring(5);
    const invoiceID = e.currentTarget.attributes.invoice.value;
    const suppress = (type === 'hide');

    await execNotes({
      data: {
        invoiceDetails: [
          {
            invoiceID,
            invoiceDetailID,
            suppress,
          },
        ],
        invoiceDetailPrice: [
          {
            invoiceID,
            invoiceDetailID,
            suppress,
          },
        ],
      },
    });

    if (!isNotesError) {
      const invoicesCopy = invoices;
      invoicesCopy.forEach((invoice) => {
        if (invoice.invoiceID === Number(invoiceID)) {
          invoice.invoiceDetails.forEach((detail) => {
            if (detail.invoiceDetailID === Number(invoiceDetailID)) {
              detail.suppress = suppress;
              detail.priceInfo.suppress = suppress;
            }
          });
        }
      });
      setInvoices(invoicesCopy);
    }
    // triggers reload for modal data
    setRandomNumber(Math.random());
  };

  const onlyCheckedInvoices = useMemo(
    () => Object.keys(checkedInvoices).reduce((acc, current) => {
      if (checkedInvoices[current]) {
        acc.push(Number(current));
      }

      return acc;
    }, []),
    [checkedInvoices]
  );

  const allInvoicesDetails = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length) {
      return [];
    }

    return flatten(map(invoices, 'invoiceDetails'));
  }, [invoices]);

  useEffect(() => {
    if (!onlyCheckedInvoices.length) {
      setCheckAllInvoicesIndicator(true);
    }
    setNoneSelected(onlyCheckedInvoices.length === 0);
    setDisabledCursorClass(onlyCheckedInvoices.length === 0 ? 'disabled-cursor' : '');

    if (onlyCheckedInvoices.length === invoices.length || onlyCheckedInvoices.length === 0) {
      setAllOrSelected('All');
    } else {
      setAllOrSelected('Selected');
    }
  }, [onlyCheckedInvoices]);

  const handleOnCheckedAllInvoices = (markAsChecked) => () => {
    const checkedAllInvoices = invoices.reduce((acc, current) => {
      acc[current.invoiceID] = markAsChecked;

      return acc;
    }, {});

    setCheckedInvoices(checkedAllInvoices);
    setCheckAllInvoicesIndicator(!markAsChecked);
    setCurrentInvoiceIndex(null);
  };

  const handleOnChangeInvoiceChecked = (invoiceId) => () => {
    setCheckedInvoices((prev) => ({
      ...prev,
      [invoiceId]: !prev[invoiceId],
    }));
  };

  const handleOnPreviewSend = () => {
    if (!invoiceGroupID || !onlyCheckedInvoices.length) {
      return;
    }
    window.sessionStorage.setItem('fromCreateInvoices', 'false');
    if (paymentProcessorType === 'p') {
      window.sessionStorage.setItem('autoChargeSource', 'edit');
    } else {
      window.sessionStorage.removeItem('autoChargeSource');
    }
    const previewRedirectPage = autoChargeEnabled && paymentProcessorType === 'p' ? 'InvoiceAutoCharge' : 'InvoiceGroupPreview';
    history.push(
      `/invoices/${previewRedirectPage}/${invoiceGroupID}/invoice/${onlyCheckedInvoices.join(
        ','
      )}`,
      { replace: true }
    );
  };

  const hasPendingPayment = (id, byLot = false) => {
    const foundInvoice = invoices.find((invoice) => invoice.invoiceID === id);
    let isPendingPayment = false;
    if (byLot) {
      if (foundInvoice.invoiceDetails.every(item => !item.onlinePaymentStat === 'u')) {
        isPendingPayment = true;
      }
    } else {
      const paymentStat = get(foundInvoice, 'onlinePaymentStat', '');
      isPendingPayment = paymentStat === 'p' || paymentStat === 'u' || paymentStat === 'h';
    }

    return isPendingPayment;
  };

  useEffect(() => {
    if (typeof invoices === 'undefined' || !invoices.length) {
      return;
    }

    // eslint-disable-next-line no-nested-ternary
    // filter removes paid invoices from edit-all modal
    const selectedInvoiceData = invoices.filter(invoice => onlyCheckedInvoices.indexOf(invoice.invoiceID) > -1);
    let filteredInvoices = [];
    if (currentInvoiceIndex !== null) {
      filteredInvoices = [invoices[currentInvoiceIndex]];
    } else if (onlyCheckedInvoices.length > 0) {
      filteredInvoices = selectedInvoiceData;
    } else {
      filteredInvoices = invoices.filter(invoice => invoice.allPaid === false);
    }

    const invoicesData = filteredInvoices.map(
      ({ invoiceID,
        firstName,
        lastName,
        invoiceDetails,
        paddleNumber,
        city,
        state,
        province,
        country,
        channel,
      }) => ({
        name: `${lastName}, ${firstName}`,
        id: invoiceID,
        outstandingBalance: getOutstandingBalancePerInvoice(invoiceID),
        paddleNumber,
        location: `${city}, ${state}${province} ${country}`,
        channel,
        country,
        isPendingPayment: hasPendingPayment(invoiceID),
        charges: invoiceDetails.map(
          ({ lotID, lotNum, lotNumExt, lotURL, priceInfo, suppress, restricted, paid, lotTitle, paymentMethod, additionalPriceInfo, onlinePaymentStat, shippingReviewID }) => ({
            suppress,
            restricted,
            paid,
            lotId: lotID,
            lotURL,
            lotDisplayName: `${lotNum}${lotNumExt}`,
            tax: priceInfo.tax || '0.00',
            shipping: priceInfo.shipping || '0.00',
            insurance: priceInfo.insurance || '0.00',
            other: priceInfo.other || '0.00',
            additionalTax: get(additionalPriceInfo, 'tax', '0.00'),
            additionalShipping: get(additionalPriceInfo, 'shipping', '0.00'),
            additionalInsurance: get(additionalPriceInfo, 'insurance', '0.00'),
            additionalOther: get(additionalPriceInfo, 'other', '0.00'),
            additionalPaid: get(additionalPriceInfo, 'paid', false),
            additionalPending: (get(additionalPriceInfo, 'onlinePaymentStat', '') === 'u'),
            pendingLot: onlinePaymentStat === 'u',
            hammer: priceInfo.hammer || '0.00',
            lotTitle,
            paymentMethod,
            isInvShipping: (shippingReviewID !== ''),
            premium: priceInfo.premium || '0.00',
            livefee: priceInfo.livefee || '0.00',
          })
        ),
      })
    );

    setInvoicesCharges(invoicesData);
  }, [invoices, currentInvoiceIndex, onlyCheckedInvoices, randomNumber]);

  const showPaddle = (paddle) => (paddle !== 0 && paddle !== '');
  const isSuperUser = user.isSuperUser !== null;

  useEffect(() => {
    if (typeof allInvoicesDetails === 'undefined') {
      return;
    }

    const getUnsuppressedCount = () => {
      let rawCount = 0;
      allInvoicesDetails.forEach(item => {
        if (!item.suppress) {
          rawCount++;
        }
      });
      return rawCount;
    };
    const getGrandTotal = () => {
      const rawValue = sumBy(
        invoices,
        (invoice) => parseFloat(preformatFloat(invoice.invoiceTotal))
      );
      return rawValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const topTotalObject = {
      invoiceTotal: getGrandTotal(),
      itemCount: getUnsuppressedCount(),
    };

    setTopTotals(topTotalObject);
  }, [allInvoicesDetails, invoices]);

  const canAddPostCharges = (invoice) => {
    let paidByPP = false;
    if (invoice.invoiceDetails.some(item => !item.restricted && (item.paymentMethod.trim() === 'paidByWePay' || item.paymentMethod.trim() === 'ACH'))) {
      paidByPP = true;
    }

    return (invoice.allPaid || allVisibleArePaid(invoice)) && !hasPendingPayment(invoice.invoiceID) && paidByPP && paymentProcessingEnabled && paymentProcessorType === 'p';
  };

  const additionalChargesPaid = (invoice) => {
    let chargesPaid = false;
    if (invoice.invoiceDetails.some(item => item.additionalPriceInfo !== null && typeof item.additionalPriceInfo === 'object' && item.additionalPriceInfo.paid)) {
      chargesPaid = true;
    }

    return chargesPaid;
  };

  const isFullyUnpaid = (id) => {
    const currentInvoice = invoices.find((invoice) => invoice.invoiceID === id);
    let returnFlag = true;

    if (currentInvoice.allPaid) {
      returnFlag = false;
    } else if (hasPendingPayment(currentInvoice.invoiceID)) {
      returnFlag = false;
    } else {
      for (let i = 0; i < currentInvoice.invoiceDetails.length; i++) {
        if (currentInvoice.invoiceDetails[i].paid || Number(currentInvoice.invoiceDetails[i].totalAmountPaid) > 0) {
          returnFlag = false;
          break;
        }
      }
    }

    return returnFlag;
  };

  const processPickupShip = async (e) => {
    // show spinner
    const list = document.getElementById(`bubbles_${e.currentTarget.id}`).classList;
    list.remove('d-none');
    // the object is used to update the screen in the useEffect below
    const linkLabel = document.getElementById(`shippingLink_${e.currentTarget.id}`);
    const newText = e.currentTarget.attributes.changestate.value === 'shipping' ? 'pickup' : 'shipping';
    const changeElement = e.currentTarget.attributes.changestate;
    const updateObject = {
      linkLabel,
      changeElement,
      newText,
      id: e.currentTarget.id,
    };
    setPickupShipUpdateObject(updateObject);

    const postData = {
      deliveryMode: e.currentTarget.attributes.changestate.value,
      invoiceID: e.currentTarget.id,
    };

    try {
      await execPickupShip({
        data: {
          invoiceItemDeliveryModes: [postData]
        }
      });
    } catch (err) {
      console.log('processPickupShip post failure', err);
    }
  };

  useEffect(() => {
    if (get(pickupShipData, 'data.data.success', false)) {
      pickupShipUpdateObject.linkLabel.innerText = titleCase(pickupShipUpdateObject.newText);
      pickupShipUpdateObject.changeElement.value = pickupShipUpdateObject.newText;

      if (queryID.length) {
        execEditPageData({
          data: queryID.split(','),
        });
      } else {
        execGroupDetailInfo();
      }
    }
  }, [pickupShipData]);

  useEffect(() => {
    const collection = document.getElementsByClassName('bubble-spinner');
    if (collection.length > 0) {
      for (let i = 0; i < collection.length; i++) {
        collection[i].classList.add('d-none');
      }
    }
  }, [invoices]);

  if (showLoader || invoices.length === 0) {
    return (
      <ReactLoading className="loading-spinner" type="spin" color="black" />
    );
  }

  return (
    <>
      <Page
        className={blockEl}
        header={{ title: '' }}
        navLink={{
          displayName: 'Back to Invoice Group Details',
          href: `/invoices/InvoiceGroupDetails/${invoiceGroupID}`,
        }}
      >
        <Container>
          {!!isInvShippingAvailable && (
            <div className="mt-n3 mb-4">
              <InvaluableShippingBanner />
            </div>
          )}
          <Row className={`${blockEl}__header`}>
            <Col xs={12} className={`${blockEl}__header-title`}>
              <h1>
                Edit Invoices
              </h1>
            </Col>
            <Col xs={12} className={`${blockEl}__totals pl-0`}>
              <div className="totals-table my-3 w-100">
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Invoices
                  </div>
                  <div>
                    {invoices.length}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Items
                  </div>
                  <div>
                    {topTotals.itemCount}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Hammer
                  </div>
                  <div>
                    {currencySymbol}{topTotalsData.hammer}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total BP
                  </div>
                  <div>
                    {currencySymbol}{topTotalsData.premium}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Live Fee
                  </div>
                  <div>
                    {currencySymbol}{topTotalsData.livefee}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Tax
                  </div>
                  <div>
                    {currencySymbol}{topTotalsData.tax}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total S/H
                  </div>
                  <div>
                    {currencySymbol}{topTotalsData.shipping}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Insurance
                  </div>
                  <div>
                    {currencySymbol}{topTotalsData.insurance}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Other
                  </div>
                  <div>
                    {currencySymbol}{topTotalsData.other}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Invoice Total
                  </div>
                  <div>
                    {currencySymbol}{formatCurrencyForLocale(topTotals.invoiceTotal)}
                  </div>
                </div>
              </div>
            </Col>
            <Col xs="6" className="ml-n3">
              {checkedAllInvoicesIndicator ? (
                <Button color="link" onClick={handleOnCheckedAllInvoices(true)}>
                  Select All Invoices
                </Button>
              ) : (
                <Button
                  color="link"
                  onClick={handleOnCheckedAllInvoices(false)}
                >
                  Deselect all
                </Button>
              )}
              {!noneSelected && (
              <Button
                color="link"
                className="ml-4"
                onClick={() => {
                  handleOnEditAddChargesToAll();
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Edit/Add Charges to {allOrSelected}
              </Button>
              )}
            </Col>
            <Col className={`${blockEl}__header-actions`}>
              <Button
                color="primary"
                onClick={handleOnPreviewSend}
                disabled={noneSelected}
                className={disabledCursorClass}
              >
                Preview &amp; Send {allOrSelected}
              </Button>
            </Col>

          </Row>
          {invoices.length
            ? invoices.map((invoice, index) => (
              <Row className={`${blockEl}__invoice`} id={invoice.invoiceID} key={invoice.invoiceID}>
                <Col xs="12" style={{ paddingBottom: 0 }}>
                  <Row className={`${blockEl}__invoice-details`}>
                    <Col xs={6}>
                      <Row>
                        <Col xs="auto" className={`${blockEl}__invoice-checkbox`}>
                          <Input
                            type="checkbox"
                            key={Math.random()}
                            onClick={handleOnChangeInvoiceChecked(invoice.invoiceID)}
                            defaultChecked={checkedInvoices[invoice.invoiceID]}
                          />
                        </Col>
                        <Col>
                          <div className={`${blockEl}__invoice-details__name`}>
                            <h3>
                              <a href={`${cfPath()}/al/bidders/viewBidder.cfm?approvalID=${invoice.approvalID}`} target="_blank" rel="noopener noreferrer">
                                {invoice.lastName}, {invoice.firstName}
                              </a>
                              {showPaddle(invoice.paddleNumber) && (
                              <span className="small ml-1">
                                (Paddle #{invoice.paddleNumber})
                              </span>
                              )}
                            </h3>
                          </div>
                          <div className="mb-2">
                            {invoice.email}
                          </div>
                          <div>
                            Invoice #{invoice.invoiceID} | {invoice.city},{' '}
                            {invoice.state || invoice.province},{' '} {invoice.country}
                            {' '}| {formatChannelText(invoice.channel)}
                          </div>
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={6} className={`${blockEl}__invoice-balance`}>
                      <div className="float-right ml-5">
                        <h4>Outstanding Balance</h4>
                        <p className="text-right">
                          {currencySymbol}{formatCurrencyForLocale(invoice.outStandingBalance)}
                        </p>
                      </div>
                      <div className="float-right ml-5 mr-3">
                        <h4>Payments Made</h4>
                        <p className="text-right">
                          {currencySymbol}{formatCurrencyForLocale(invoice.invoicePaid)}
                        </p>
                      </div>
                      <div className="float-right mr-3">
                        <h4>Invoice Total</h4>
                        <p className="text-right">
                          {currencySymbol}{formatCurrencyForLocale(invoice.invoiceTotal)}
                        </p>
                      </div>
                      <div className="clearfix" />
                    </Col>
                  </Row>
                  <Row>
                    <Col
                      xs={12}
                      className={`${blockEl}__invoice-details__actions`}
                      onClick={handleOnSetCurrentInvoiceIndex(index)}
                    >
                      {canEditInvoiceCharges(invoice) && !allVisibleArePaid(invoice) && !hasPendingPayment(invoice.invoiceID, true) && (
                      <div className="float-left mr-5 ml-4">
                        <Button
                          color="link"
                          onClick={() => {
                            setLotVariant('lot');
                            setIsAddChargesModalOpen(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                          Edit/Add charges
                        </Button>
                      </div>
                      )}
                      {canAddPostCharges(invoice) && !additionalChargesPaid(invoice) && !hasPendingPayment(invoice.invoiceID) && (
                      <div className="float-left mr-5 ml-4">
                        <Button
                          color="link"
                          onClick={() => {
                            setLotVariant('lot-additional');
                            setIsAddChargesModalOpen(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                          Edit/Add charges
                        </Button>
                      </div>
                      )}
                      <div className="float-left">
                        <Button
                          color="link"
                          onClick={() => {
                            setIsContactModalOpen(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faEnvelope} />
                          Contact
                        </Button>
                      </div>
                      <div className="float-left ml-5">
                        <Button
                          color="link"
                          onClick={() => {
                            setIsAddNoteModalOpen(true);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faStickyNote}
                            className={invoice.notes !== '' ? 'has-note' : ''}
                            id={`noteIcon_${invoice.invoiceID}`}
                          />
                          {invoice.notes !== '' ? 'Update' : 'Add'} internal note
                        </Button>
                      </div>
                      {isFullyUnpaid(invoice.invoiceID) && (
                        <>
                          <div className="float-left ml-5">
                            <Button
                              color="link"
                              id={invoice.invoiceID}
                              changestate={invoice.pickup === undefined || !invoice.pickup ? 'pickup' : 'shipping'}
                              onClick={(e) => { processPickupShip(e); }}
                            >
                              <FontAwesomeIcon icon={faTruck} />
                              Mark as <span id={`shippingLink_${invoice.invoiceID}`}>{titleCase(invoice.pickup === undefined || !invoice.pickup ? 'pickup' : 'shipping')}</span>
                            </Button>
                          </div>
                          <div id={`bubbles_${invoice.invoiceID}`} className="float-left bubble-spinner d-none">
                            <ReactLoading
                              className="mt-1"
                              type="bubbles"
                              color="black"
                              height="45px"
                              width="45px"
                            />
                          </div>
                        </>
                      )}
                    </Col>
                  </Row>
                  {paymentProcessingEnabled && invoiceHasRestrictedItems(invoice.invoiceID) && (
                    <div className="alert alert-warning m2-3" role="alert">
                      <span className="alert-content">
                        <FontAwesomeIcon icon="credit-card" className="" />
                        <span className="pl-2">
                          One or more item(s) in this invoice are restricted and cannot be paid for online. Additional charges will need to be added on an item level.
                        </span>
                      </span>
                    </div>
                  )}
                </Col>
                <Col xs="12">
                  <Row>
                    <Table
                      bordered
                      className={`${blockEl}__invoice__amounts`}
                    >
                      <thead>
                        <tr>
                          <th>Session</th>
                          <th>Lot</th>
                          <th>Hammer</th>
                          <th>BP</th>
                          {showColumn('livefee', invoice.invoiceID) && (
                            <th>Live Fee</th>
                          )}
                          {showColumn('tax', invoice.invoiceID) && (
                            <th>Tax</th>
                          )}
                          {showColumn('shipping', invoice.invoiceID) && (
                            <th>S/H</th>
                          )}
                          {showColumn('insurance', invoice.invoiceID) && (
                            <th>Insurance</th>
                          )}
                          {showColumn('other', invoice.invoiceID) && (
                            <th>
                              Other {' '}
                              <FontAwesomeIcon
                                icon={faQuestionCircle}
                                id="otherTooltip"
                                style={{ cursor: 'pointer' }}
                              />
                              <UncontrolledTooltip
                                placement="right"
                                target="otherTooltip"
                                className="other-tooltip"
                              >
                                This column is for adding or discounting any additional fees.
                              </UncontrolledTooltip>
                            </th>
                          )}
                          <th>Total</th>
                          {showDisputesColumn(invoice.invoiceID) && (
                            <th>Disputes</th>
                          )}
                          <th> Invoice Status</th>
                          {!!isInvShippingAvailable && (
                            <th>Shipping Status</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.invoiceDetails
                          && invoice.invoiceDetails.length
                          ? invoice.invoiceDetails.map((invoiceDetail) => (
                            <tr
                              key={invoiceDetail.invoiceDetailID}
                              className={classNames(`${blockEl}__invoice__item`, {
                                [`${blockEl}__invoice__hidden-item`]: invoiceDetail.suppress || invoiceDetail.priceInfo.suppress,
                              })}
                            >
                              <td>{invoiceDetail.saleIdentifier}</td>
                              <td id={`lotnum_${invoiceDetail.lotRef}`}>
                                <Button color="link">
                                  <a
                                    className={classNames({
                                      [`${blockEl}__invoice__hidden-item`]: invoiceDetail.suppress || invoiceDetail.priceInfo.suppress
                                    })}
                                    href={invoiceDetail.lotURL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {invoiceDetail.lotNum}
                                    {invoiceDetail.lotNumExt}
                                  </a>
                                </Button>
                                {paymentProcessingEnabled && invoiceDetail.restricted && ('*')}
                                <UncontrolledTooltip placement="right" target={`lotnum_${invoiceDetail.lotRef}`}>
                                  {invoiceDetail.lotTitle}
                                </UncontrolledTooltip>
                              </td>
                              <td>
                                <Row className="currency-table-cell">
                                  <Col>{currencySymbol}</Col>
                                  <Col>
                                    {formatCurrencyForLocale(invoiceDetail.priceInfo.hammer)}
                                  </Col>
                                </Row>
                              </td>
                              <td>
                                <Row className="currency-table-cell">
                                  <Col>{currencySymbol}</Col>
                                  <Col>
                                    {formatCurrencyForLocale(invoiceDetail.priceInfo.premium)}
                                  </Col>
                                </Row>
                              </td>
                              {showColumn('livefee', invoice.invoiceID) && (
                                <td>
                                  <Row className="currency-table-cell">
                                    <Col>{currencySymbol}</Col>
                                    <Col>
                                      {formatCurrencyForLocale(invoiceDetail.priceInfo.livefee)}
                                    </Col>
                                  </Row>
                                </td>
                              )}
                              {showColumn('tax', invoice.invoiceID) && (
                                <td>
                                  <Row className="currency-table-cell">
                                    <Col>{currencySymbol}</Col>
                                    <Col>{formatCurrencyForLocale(invoiceDetail.priceInfo.tax)}</Col>
                                  </Row>
                                </td>
                              )}
                              {showColumn('shipping', invoice.invoiceID) && (
                                <td>
                                  <Row className="currency-table-cell">
                                    <Col>{currencySymbol}</Col>
                                    <Col>{formatCurrencyForLocale(invoiceDetail.priceInfo.shipping)}</Col>
                                  </Row>
                                </td>
                              )}
                              {showColumn('insurance', invoice.invoiceID) && (
                                <td>
                                  <Row className="currency-table-cell">
                                    <Col>{currencySymbol}</Col>
                                    <Col>{formatCurrencyForLocale(invoiceDetail.priceInfo.insurance)}</Col>
                                  </Row>
                                </td>
                              )}
                              {showColumn('other', invoice.invoiceID) && (
                                <td>
                                  <Row className="currency-table-cell">
                                    <Col>{currencySymbol}</Col>
                                    <Col>{formatCurrencyForLocale(invoiceDetail.priceInfo.other)}</Col>
                                  </Row>
                                </td>
                              )}
                              <td>
                                <Row className="currency-table-cell">
                                  <Col>{currencySymbol}</Col>
                                  <Col>
                                    {formatCurrencyForLocale(invoiceDetail.totalAmount)}
                                  </Col>
                                </Row>
                              </td>
                              {showDisputesColumn(invoice.invoiceID) && (
                                <td>
                                  {invoiceDetail.disputeRef && (
                                    <Button color="link">
                                      <a href={`/ah/customers/dispute/${invoiceDetail.disputeRef}`} target="_blank" rel="noopener noreferrer">
                                        View
                                      </a>
                                    </Button>
                                  )}
                                </td>
                              )}
                              <td>
                                <Table borderless size="sm" className="status-table margin-top-0">
                                  <tbody>
                                    <tr>
                                      <td>
                                        {getInvoiceDetailStatus(
                                          invoice.invoiceSentDateString,
                                          invoiceDetail.paidDateString,
                                          invoiceDetail.onlinePaymentStat,
                                          invoiceDetail.paid
                                        )}
                                        {invoiceDetail.paid && invoiceDetail.paymentMethod.trim() === 'paidByWePay' && (
                                        <span className=" ml-3">
                                          <FontAwesomeIcon icon={faCreditCard} className="fa-lg" />
                                        </span>
                                        )}
                                        <Dropdown isOpen={isOpenInvoiceItemStatusMenu[invoiceDetail.invoiceDetailID] || false} direction="left" toggle={handleOnOpenInvoiceItemStatusMenu(invoiceDetail.invoiceDetailID)}>
                                          <DropdownToggle>
                                            <FontAwesomeIcon icon={faEllipsisV} />
                                          </DropdownToggle>

                                          <DropdownMenu className={`${blockEl}__invoice__dropdown-link`}>
                                            {!invoiceDetail.paid && !hasPendingPayment(invoice.invoiceID) && !allVisibleArePaid(invoice) && (
                                              <DropdownItem
                                                invoiceid={invoice.invoiceID}
                                                detailid={invoiceDetail.invoiceDetailID}
                                                date={invoiceDetail.paidDateString}
                                                paymentmethodid={invoiceDetail.paymentMethodID}
                                                variant="Paid"
                                                onClick={(e) => { openCalendar(e); }}
                                                currentstatus={invoiceDetail.paid.toString()}
                                              >
                                                Mark as paid
                                              </DropdownItem>
                                            )}
                                            {invoiceDetail.paid && (invoiceDetail.paymentMethod.trim() !== 'paidByWePay' && invoiceDetail.paymentMethod.trim() !== 'ACH') && (
                                              <DropdownItem
                                                invoiceid={invoice.invoiceID}
                                                detailid={invoiceDetail.invoiceDetailID}
                                                date={invoiceDetail.paidDateString}
                                                paymentmethodid={invoiceDetail.paymentMethodID}
                                                variant="Paid"
                                                onClick={(e) => { openCalendar(e); }}
                                                currentstatus="true"
                                              >
                                                Mark as not paid
                                              </DropdownItem>
                                            )}

                                            {!invoiceDetail.shipped && (
                                              <DropdownItem
                                                invoiceid={invoice.invoiceID}
                                                detailid={invoiceDetail.invoiceDetailID}
                                                date={invoiceDetail.shippedDateString}
                                                paymentmethodid={0}
                                                variant="Shipped"
                                                currentstatus={invoiceDetail.shipped.toString()}
                                                onClick={(e) => { openCalendar(e); }}
                                              >
                                                Mark as shipped
                                              </DropdownItem>
                                            )}
                                            {!invoiceDetail.paid && !hasPendingPayment(invoice.invoiceID) && !allVisibleArePaid(invoice) && (
                                              (invoiceDetail.suppress || invoiceDetail.priceInfo.suppress) ? (
                                                <DropdownItem
                                                  id={`show_${invoiceDetail.invoiceDetailID}`}
                                                  invoice={invoice.invoiceID}
                                                  onClick={(e) => { hideShowItem(e); }}
                                                >
                                                  Show on invoice
                                                </DropdownItem>
                                              ) : (
                                                <DropdownItem
                                                  id={`hide_${invoiceDetail.invoiceDetailID}`}
                                                  invoice={invoice.invoiceID}
                                                  onClick={(e) => { hideShowItem(e); }}
                                                >
                                                  Hide from invoice
                                                </DropdownItem>
                                              )
                                            )}
                                            {(invoiceDetail.canOpenDispute || isSuperUser) && !invoiceDetail.disputeRef && invoice.channel !== 'Floor Bidders' && (
                                            <DropdownItem onClick={() => { window.open(`/ah/reports/openDisputes/${invoiceDetail.lotRef}`, '_blank'); }}>
                                              File as dispute
                                            </DropdownItem>
                                            )}
                                          </DropdownMenu>
                                        </Dropdown>
                                      </td>
                                    </tr>
                                    {invoiceDetail.shipped && (
                                      <tr>
                                        <td>
                                          Shipped {invoiceDetail.shippedDateString && (
                                          ` ${formatDateString(invoiceDetail.shippedDateString)}`
                                        )}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </td>
                              {!!isInvShippingAvailable && (
                                <td className="border-bottom-0">
                                  {formatShippingMethod(invoiceDetail.shippingShipmentID)}
                                </td>
                              )}
                            </tr>
                          ))
                          : null}
                      </tbody>
                    </Table>
                  </Row>
                </Col>
                {paymentProcessingEnabled && invoiceHasRestrictedItems(invoice.invoiceID) && (
                  <div className="ml-2 mt-3">
                    *this item is restricted and cannot be paid for online
                  </div>
                )}

                {canAddPostCharges(invoice) && (
                <AdditionalChargesTable invoice={invoice} currencySymbol={currencySymbol} />
                )}
              </Row>
            ))
            : null}
        </Container>
      </Page>
      <AddChargesModal
        isOpen={isAddChargesModalOpen}
        invoices={invoicesCharges}
        byInvoiceCharges={invoicesCharges}
        onClose={() => {
          setIsAddChargesModalOpen(false);
        }}
        variant={lotVariant}
        currencySymbol={currencySymbol}
        onSave={handleOnSaveInvoiceCharges}
        paymentProcessorType={paymentProcessorType}
        setIsAddChargesModalOpen={setIsAddChargesModalOpen}
        hideAdditionalCode={false}
        chargesByLot={true}
      />
      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        notes={currentInvoiceNotes}
        modalNameObject={modalNameObject}
        onClose={() => {
          setIsAddNoteModalOpen(false);
        }}
        onSave={handleOnAddNote}
      />
      <ContactModal
        isOpen={isContactModalOpen}
        modalNameObject={modalNameObject}
        onClose={() => {
          setIsContactModalOpen(false);
        }}
        onSave={handleOnSendMessage}
      />
      <CalendarModal
        isOpen={isCalendarModalOpen}
        invoiceID={calendarID}
        invoiceDetailID={calendarDetailID}
        variant={calendarVariant}
        paymentMethods={paymentMethods}
        paymentMethodID={calendarPaymentMethod}
        presetDate={calendarDate}
        onClose={() => {
          setIsCalendarModalOpen(false);
        }}
        onSave={(calendarVariant === 'Shipped') ? handleSetShippedDate : handleSetPaidDate}
        currentStatus={calendarCurrentStatus}
      />
    </>
  );
};

export default EditByLot;
